import axios from "axios";
import { Base_URL } from "../url";

// ============================================================================
// STAGE MASTER APIs
// ============================================================================

// Get all stages (only active)
export const fetchStages = async () => {
  try {
    const response = await axios.get(`${Base_URL}/StageMaster/GetAllStages`);
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error fetching stages!";
  }
};

// Get all stages including deleted (admin only)
export const fetchAllStagesIncludingDeleted = async () => {
  try {
    const response = await axios.get(
      `${Base_URL}/StageMaster/GetAllStagesIncludingDeleted`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error fetching stages!";
  }
};

// Get stages by organization
export const fetchStagesByOrg = async (orgId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/StageMaster/GetStagesByOrg/${orgId}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error fetching stages by organization!";
  }
};

// Get stages by project
export const fetchStagesByProject = async (projectId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/StageMaster/GetStagesByProject/${projectId}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error fetching stages by project!";
  }
};

// Get stage by ID
export const fetchStageDetails = async (stageId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/StageMaster/GetStageById/${stageId}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error fetching stage details!";
  }
};

// Create new stage
export const createStage = async (stageData) => {
  try {
    const response = await axios.post(
      `${Base_URL}/StageMaster/CreateStage`,
      stageData,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error creating stage!";
  }
};

// Update stage
export const updateStage = async (stageId, stageData) => {
  try {
    const response = await axios.put(
      `${Base_URL}/StageMaster/UpdateStage/${stageId}`,
      stageData,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error updating stage!";
  }
};

// Soft delete stage
export const softDeleteStage = async (stageId, deletedBy, reason = "") => {
  try {
    const response = await axios.delete(
      `${Base_URL}/StageMaster/SoftDeleteStage/${stageId}`,
      {
        params: {
          deletedBy: deletedBy,
          reason: reason,
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error deleting stage!";
  }
};

// Restore stage
export const restoreStage = async (stageId, restoredBy, reason = "") => {
  try {
    const response = await axios.put(
      `${Base_URL}/StageMaster/RestoreStage/${stageId}`,
      null,
      {
        params: {
          restoredBy: restoredBy,
          reason: reason,
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error restoring stage!";
  }
};

// Hard delete stage (permanent)
export const hardDeleteStage = async (stageId) => {
  try {
    const response = await axios.delete(
      `${Base_URL}/StageMaster/HardDeleteStage/${stageId}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error permanently deleting stage!";
  }
};

// Delete stages by project (soft delete)
export const deleteStagesByProject = async (projectId) => {
  try {
    const response = await axios.delete(
      `${Base_URL}/StageMaster/DeleteStagesByProject/${projectId}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error deleting stages by project!";
  }
};

// Add comment to stage
export const addStageComment = async (commentData) => {
  try {
    const response = await axios.post(
      `${Base_URL}/StageMaster/AddCommentByStage`,
      commentData,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error adding stage comment!";
  }
};

// Get comments for a stage
export const fetchStageComments = async (stageId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/StageMaster/GetStageComments/${stageId}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error fetching stage comments!";
  }
};

// Get stage activity timeline
export const fetchStageTimeline = async (stageId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/StageMaster/GetStageTimeline/${stageId}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error fetching stage timeline!";
  }
};

// ============================================================================
// STAGE TYPE MASTER APIs
// ============================================================================

/**
 * Get all stage types by organization
 * @param {number} organisationId - Organization ID
 * @returns {Promise<Array>} List of stage types
 */
export const fetchStageTypesByOrganisation = async (organisationId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/StageTypeMaster/GetStageTypesByOrganisation/${organisationId}`,
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return [];
    }
    console.error("Error fetching stage types:", error);
    throw error.response?.data || "Error fetching stage types by organisation!";
  }
};

/**
 * Get stage type by GUID
 * @param {string} stageTypeGuid - Stage type GUID
 * @returns {Promise<Object>} Stage type data
 */
export const fetchStageTypeByGuid = async (stageTypeGuid) => {
  try {
    const response = await axios.get(
      `${Base_URL}/StageTypeMaster/GetStageTypeByGuid/${stageTypeGuid}`,
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error("Error fetching stage type by GUID:", error);
    throw error.response?.data || "Error fetching stage type by GUID!";
  }
};

/**
 * Get stage type by ID
 * @param {number} id - Stage type ID
 * @returns {Promise<Object>} Stage type data
 */
export const fetchStageTypeById = async (id) => {
  try {
    // Note: You may need to add this endpoint to your backend controller
    const response = await axios.get(
      `${Base_URL}/StageTypeMaster/GetStageTypeById/${id}`,
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error("Error fetching stage type by ID:", error);
    throw error.response?.data || "Error fetching stage type by ID!";
  }
};

/**
 * Get all stage types (super admin only)
 * @returns {Promise<Array>} List of all stage types
 */
export const fetchAllStageTypes = async () => {
  try {
    const response = await axios.get(
      `${Base_URL}/StageTypeMaster/GetAllStageTypes`,
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return [];
    }
    console.error("Error fetching all stage types:", error);
    throw error.response?.data || "Error fetching all stage types!";
  }
};

/**
 * Create a new stage type
 * @param {Object} stageTypeData - Stage type data
 * @param {string} stageTypeData.stageTypeName - Stage type name
 * @param {string} stageTypeData.createdBy - Created by user ID
 * @param {number} stageTypeData.organisationId - Organization ID
 * @returns {Promise<Object>} Created stage type
 */
export const createStageType = async (stageTypeData) => {
  try {
    const response = await axios.post(
      `${Base_URL}/StageTypeMaster/CreateStageType`,
      {
        stageTypeName: stageTypeData.stageTypeName,
        createdBy: stageTypeData.createdBy,
        organisationId: stageTypeData.organisationId,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error creating stage type:", error);
    throw error.response?.data || "Error creating stage type!";
  }
};

/**
 * Update a stage type
 * @param {number} id - Stage type ID
 * @param {Object} stageTypeData - Stage type data
 * @param {string} stageTypeData.stageTypeName - Stage type name
 * @param {string} stageTypeData.changedBy - Changed by user ID
 * @returns {Promise<Object>} Updated stage type
 */
export const updateStageType = async (id, stageTypeData) => {
  try {
    const response = await axios.put(
      `${Base_URL}/StageTypeMaster/UpdateStageType/${id}`,
      {
        stageTypeName: stageTypeData.stageTypeName,
        changedBy: stageTypeData.changedBy,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error updating stage type:", error);
    throw error.response?.data || "Error updating stage type!";
  }
};

/**
 * Delete a stage type
 * @param {number} id - Stage type ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteStageType = async (id) => {
  try {
    const response = await axios.delete(
      `${Base_URL}/StageTypeMaster/DeleteStageType/${id}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting stage type:", error);
    throw error.response?.data || "Error deleting stage type!";
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get stage type name from GUID (with caching)
 * @param {string} stageTypeGuid - Stage type GUID
 * @param {number} organisationId - Organization ID
 * @returns {Promise<string>} Stage type name
 */
const stageTypeNameCache = {};

export const getStageTypeName = async (stageTypeGuid, organisationId) => {
  if (!stageTypeGuid) return "Not set";

  // Check cache first
  const cacheKey = `${organisationId}_${stageTypeGuid}`;
  if (stageTypeNameCache[cacheKey]) {
    return stageTypeNameCache[cacheKey];
  }

  try {
    // Try to get by GUID first
    let stageType = await fetchStageTypeByGuid(stageTypeGuid);

    // If not found and we have organisationId, try to get from list
    if (!stageType && organisationId) {
      const stageTypes = await fetchStageTypesByOrganisation(organisationId);
      stageType = stageTypes.find((st) => st.stageTypeGuid === stageTypeGuid);
    }

    const name = stageType?.stageTypeName || stageTypeGuid.substring(0, 8);
    stageTypeNameCache[cacheKey] = name;
    return name;
  } catch (error) {
    console.error("Error getting stage type name:", error);
    return stageTypeGuid.substring(0, 8);
  }
};

/**
 * Get stage type GUID from name
 * @param {string} stageTypeName - Stage type name
 * @param {number} organisationId - Organization ID
 * @returns {Promise<string|null>} Stage type GUID
 */
export const getStageTypeGuid = async (stageTypeName, organisationId) => {
  if (!stageTypeName || !organisationId) return null;

  try {
    const stageTypes = await fetchStageTypesByOrganisation(organisationId);
    const stageType = stageTypes.find(
      (st) => st.stageTypeName === stageTypeName,
    );
    return stageType?.stageTypeGuid || null;
  } catch (error) {
    console.error("Error getting stage type GUID:", error);
    return null;
  }
};

// import axios from "axios";
// import { Base_URL } from "../url";

// // Get all stages (only active)
// export const fetchStages = async () => {
//   try {
//     const response = await axios.get(`${Base_URL}/StageMaster/GetAllStages`);
//     return response.data;
//   } catch (error) {
//     throw error.response?.data || "Error fetching stages!";
//   }
// };

// // Get all stages including deleted (admin only)
// export const fetchAllStagesIncludingDeleted = async () => {
//   try {
//     const response = await axios.get(
//       `${Base_URL}/StageMaster/GetAllStagesIncludingDeleted`,
//     );
//     return response.data;
//   } catch (error) {
//     throw error.response?.data || "Error fetching stages!";
//   }
// };

// // Get stages by organization
// export const fetchStagesByOrg = async (orgId) => {
//   try {
//     const response = await axios.get(
//       `${Base_URL}/StageMaster/GetStagesByOrg/${orgId}`,
//     );
//     return response.data;
//   } catch (error) {
//     throw error.response?.data || "Error fetching stages by organization!";
//   }
// };

// // Get stages by project
// export const fetchStagesByProject = async (projectId) => {
//   try {
//     const response = await axios.get(
//       `${Base_URL}/StageMaster/GetStagesByProject/${projectId}`,
//     );
//     return response.data;
//   } catch (error) {
//     throw error.response?.data || "Error fetching stages by project!";
//   }
// };

// // Get stage by ID
// export const fetchStageDetails = async (stageId) => {
//   try {
//     const response = await axios.get(
//       `${Base_URL}/StageMaster/GetStageById/${stageId}`,
//     );
//     return response.data;
//   } catch (error) {
//     throw error.response?.data || "Error fetching stage details!";
//   }
// };

// // Create new stage
// export const createStage = async (stageData) => {
//   try {
//     const response = await axios.post(
//       `${Base_URL}/StageMaster/CreateStage`,
//       stageData,
//     );
//     return response.data;
//   } catch (error) {
//     throw error.response?.data || "Error creating stage!";
//   }
// };

// // Update stage
// export const updateStage = async (stageId, stageData) => {
//   try {
//     const response = await axios.put(
//       `${Base_URL}/StageMaster/UpdateStage/${stageId}`,
//       stageData,
//     );
//     return response.data;
//   } catch (error) {
//     throw error.response?.data || "Error updating stage!";
//   }
// };

// // Soft delete stage
// export const softDeleteStage = async (stageId, deletedBy, reason = "") => {
//   try {
//     const response = await axios.delete(
//       `${Base_URL}/StageMaster/SoftDeleteStage/${stageId}`,
//       {
//         params: {
//           deletedBy: deletedBy,
//           reason: reason,
//         },
//       },
//     );
//     return response.data;
//   } catch (error) {
//     throw error.response?.data || "Error deleting stage!";
//   }
// };

// // Restore stage
// export const restoreStage = async (stageId, restoredBy, reason = "") => {
//   try {
//     const response = await axios.put(
//       `${Base_URL}/StageMaster/RestoreStage/${stageId}`,
//       null,
//       {
//         params: {
//           restoredBy: restoredBy,
//           reason: reason,
//         },
//       },
//     );
//     return response.data;
//   } catch (error) {
//     throw error.response?.data || "Error restoring stage!";
//   }
// };

// // Hard delete stage (permanent)
// export const hardDeleteStage = async (stageId) => {
//   try {
//     const response = await axios.delete(
//       `${Base_URL}/StageMaster/HardDeleteStage/${stageId}`,
//     );
//     return response.data;
//   } catch (error) {
//     throw error.response?.data || "Error permanently deleting stage!";
//   }
// };

// // Delete stages by project (soft delete)
// export const deleteStagesByProject = async (projectId) => {
//   try {
//     const response = await axios.delete(
//       `${Base_URL}/StageMaster/DeleteStagesByProject/${projectId}`,
//     );
//     return response.data;
//   } catch (error) {
//     throw error.response?.data || "Error deleting stages by project!";
//   }
// };

// // Add comment to stage
// export const addStageComment = async (commentData) => {
//   try {
//     const response = await axios.post(
//       `${Base_URL}/StageMaster/AddCommentByStage`,
//       commentData,
//     );
//     return response.data;
//   } catch (error) {
//     throw error.response?.data || "Error adding stage comment!";
//   }
// };

// // Get comments for a stage
// export const fetchStageComments = async (stageId) => {
//   try {
//     const response = await axios.get(
//       `${Base_URL}/StageMaster/GetStageComments/${stageId}`,
//     );
//     return response.data;
//   } catch (error) {
//     throw error.response?.data || "Error fetching stage comments!";
//   }
// };

// // Get stage activity timeline
// export const fetchStageTimeline = async (stageId) => {
//   try {
//     const response = await axios.get(
//       `${Base_URL}/StageMaster/GetStageTimeline/${stageId}`,
//     );
//     return response.data;
//   } catch (error) {
//     throw error.response?.data || "Error fetching stage timeline!";
//   }
// };
